/**
 * EditorPanelElement — a textured panel (image / video / alpha-video) in the
 * editor, built on EditorContentElement (web-core's CreatorElement base).
 *
 * The anchor (from the base) is the transform/gizmo/pick handle; the visual is a
 * unit plane child of it, scaled by the anchor's [w, h, 1]. Materials are the
 * production unlit / alpha-video shaders shared from web-core's content.common
 * (crisp, unlit — no gamma fade), matching the previous EditorElement exactly.
 */

import {
  MeshBuilder,
  StandardMaterial,
  Color3,
  Vector3,
  type Mesh,
  type Material,
  type Texture,
  type VideoTexture,
} from 'babylonjs';
import {
  initAlphaVideoShaderMaterial,
  initUnlitTextureShaderMaterial,
} from '@webcore/content/core/content.common';
import { AssetTypes } from '@shared/constants';
import EditorContentElement from './EditorContentElement';

export class EditorPanelElement extends EditorContentElement {
  private plane: Mesh | null = null;
  private material: Material | null = null;

  protected scaleVector(scale: number[]): Vector3 {
    // The data scale carries panel size + aspect; z is always 1 (flat).
    return new Vector3(scale[0], scale[1], 1);
  }

  async build(): Promise<void> {
    this.buildVisual();
    const asset = await this.assetManager.getAsset(this.assetId);
    if (this._disposed) return;
    if (!asset || !asset.texture) {
      console.warn(`Editor: skipping panel ${this.sceneID} — asset not available`);
      this.dispose();
      return;
    }

    this.material?.dispose();
    if (asset.assetType === AssetTypes.VIDEO_ALPHA) {
      this.material = initAlphaVideoShaderMaterial(this.scene, asset.texture as VideoTexture);
    } else {
      // IMAGE / VIDEO_OPAQUE: raw unlit texture shader (matches web-core panels).
      this.material = initUnlitTextureShaderMaterial(this.scene, asset.texture as Texture);
    }
    this.material.backFaceCulling = false;
    if (this.plane) this.plane.material = this.material;

    // Grow to target scale.
    this.tweenScaleTo(this.targetScale);
  }

  /** Build the plane + selection overlay + placeholder material (synchronous). */
  private buildVisual(): void {
    const metadata = { editorElement: this, sceneID: this.sceneID };

    this.plane = MeshBuilder.CreatePlane(`panel_${this.sceneID}`, { size: 1 }, this.scene);
    this.plane.parent = this.anchor;
    this.plane.isPickable = true;
    this.plane.metadata = metadata;

    // Selection/hover highlight: a thin yellow box enclosing the panel, child of
    // the anchor so it inherits the [w,h,1] scale.
    this.overlay = MeshBuilder.CreateBox(`panel_${this.sceneID}_overlay`, { width: 1, height: 1, depth: 0.1 }, this.scene);
    this.overlay.parent = this.anchor;
    this.overlay.isPickable = false;
    this.overlayMat = new StandardMaterial(`panel_${this.sceneID}_overlay_mat`, this.scene);
    this.overlayMat.disableLighting = true;
    this.overlayMat.emissiveColor = new Color3(1, 1, 0); // yellow
    this.overlayMat.backFaceCulling = false;
    this.overlayMat.alpha = 0;
    this.overlayMat.disableDepthWrite = true; // render on top of the panel
    this.overlay.material = this.overlayMat;

    // Placeholder material until the asset loads (white).
    const placeholder = new StandardMaterial(`panel_${this.sceneID}_placeholder`, this.scene);
    placeholder.disableLighting = true;
    placeholder.emissiveColor = new Color3(1, 1, 1);
    placeholder.backFaceCulling = false;
    this.material = placeholder;
    this.plane.material = placeholder;
  }

  /** Dim across StandardMaterial.alpha or the alpha-video shader's `alpha` uniform. */
  setOpacity(opacity: number): void {
    const mat = this.material as (StandardMaterial & { setFloat?: (n: string, v: number) => void; getClassName?: () => string }) | null;
    if (mat && typeof mat.setFloat === 'function' && mat.getClassName?.() === 'ShaderMaterial') {
      mat.setFloat('alpha', opacity);
    } else if (mat) {
      mat.alpha = opacity;
    }
  }

  protected disposeVisual(): void {
    this.material?.dispose();
    this.material = null;
    this.plane = null; // disposed with the anchor subtree
  }
}

export default EditorPanelElement;
